/**
 * "What is…?" and "What's the difference between…?": two or three plain
 * sentences in Noticeboard's own words, grounded in CONTEXT.md and the real
 * per-role page access (`sidebarConfig`), never an invented ability. These
 * never start a task or a lookup about a person.
 */
import { getSidebarItem, sidebarConfig } from "../../../config/sidebarConfig";
import { canOpenSidebarItem } from "../../navigation/sidebarAccess";
import { advertisementsQuery } from "../../../services/queries";
import { isAdActive } from "../../dashboard/advertisementActivity";
import { viewQuery } from "../scopedQuery";
import { COURSE_PATH } from "../../help/course";
import { CERTIFICATES } from "../../../routes";
import { askedHow, askedWhat } from "./askedHow";
import { defineEntry } from "./types";
import { aRole, listOf } from "./wording";

const createPage = getSidebarItem("create");
const openCreate = () => ({ label: `Open ${createPage.label}`, href: createPage.path });

/** "Are you Nova?" is this chat. Nova only rewrites Notice text in Create. */
const novaIntro = (asked: string) =>
  /\bare you\b|\byou nova\b|\bsame as\b|\bthis chat\b|\bnot nova\b/i.test(asked)
    ? "No. This chat is Ask Noticeboard. "
    : "";
/** "The difference between you and the course": the course is lessons, this chat answers questions. */
const withChatApart = (body: string, asked = "") =>
  /\byou\b|\bthis chat\b/i.test(asked)
    ? `${body} This chat is Ask Noticeboard: it answers questions here, while the course is lessons in Help Center.`
    : body;
const openCertificates = () => ({ label: "Open Certificates", href: CERTIFICATES });

const circlesPage = getSidebarItem("circles");
const openCircles = () => ({ label: `Open ${circlesPage.label}`, href: circlesPage.path });

const noticesPage = getSidebarItem("notices");
const openNotices = () => ({ label: `Open ${noticesPage.label}`, href: noticesPage.path });

const drivePage = getSidebarItem("drive");
const openDrive = () => ({ label: `Open ${drivePage.label}`, href: drivePage.path });

const usersPage = getSidebarItem("users");
const openUsers = () => ({ label: `Open ${usersPage.label}`, href: usersPage.path });

const teamPage = getSidebarItem("team");
const openTeam = () => ({ label: `Open ${teamPage.label}`, href: teamPage.path });

const homePage = getSidebarItem("home");
const openHome = () => ({ label: `Open ${homePage.label}`, href: homePage.path });
const adCenterPage = getSidebarItem("adCenter");
const activeAdverts = (n: number) => (n === 0 ? "no active adverts" : n === 1 ? "1 active advert" : `${n} active adverts`);

// Grouped by sidebar item, and inserted into the catalogue at each one's own
// place, so the whole catalogue stays in sidebar order.

export const conceptsHomeEntries = [
  // The help article "What is Noticeboard?", not a tour of the assistant.
  defineEntry({
    id: "concepts.whatIsNoticeboard",
    sidebarItem: "home",
    examples: [
      "What is Noticeboard?",
      "what is this app",
      "What's this app?",
      "What's Noticeboard?",
      "What does Noticeboard do?",
      "What is Noticeboard for?",
      "What's Noticeboard for?",
      "Tell me what Noticeboard is",
      "Tell me about Noticeboard",
      "Explain Noticeboard",
      "What is the Noticeboard dashboard?",
      "What is Noticeboard used for?",
      "Can you explain Noticeboard?",
      "tell me what this website does",
      "explain noticeboard to me like im new",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () =>
          "Noticeboard is how schools, churches and other organizations keep their community informed. Your Team writes Notices on this dashboard, and the people you've invited (your Users, usually parents) get them in the Noticeboard app. You can send to Everyone in your Organization or just to the Circles that need to know.",
        () =>
          "It's the place your Organization sends Notices from. Parents and other Users receive them in the Noticeboard app, either because they're in a Circle or because you sent to Everyone.",
        () =>
          "Noticeboard is your Organization's way of reaching parents: Notices are written here, and they arrive in the Noticeboard app.",
      ],
      link: () => ({ label: "What is Noticeboard?", href: "/help/noticeboard" }),
    },
  }),

  defineEntry<{ pages: string }>({
    id: "concepts.pagesICanOpen",
    sidebarItem: "home",
    examples: [
      "What pages can I open?",
      "What's in the menu?",
      "Where can I go?",
      "Show me the sidebar",
      "Which buttons do I have?",
      "What can I open?",
      "Which pages do I have?",
      "What's in my sidebar?",
      "What menu items can I open?",
      "Which pages can I see?",
      ...askedHow("see my sidebar"),
    ],
    answer: {
      load: async (_, ctx) => ({
        pages: listOf(
          sidebarConfig.navItems.filter((item) => canOpenSidebarItem(item, ctx.user)).map((item) => item.label)
        ),
      }),
      templates: [
        ({ pages }) => `You can open ${pages}.`,
        ({ pages }) => `Your sidebar has ${pages}.`,
        ({ pages }) => `The pages you can open are ${pages}.`,
      ],
      link: openHome,
    },
  }),

  defineEntry({
    id: "concepts.whereIsAsk",
    sidebarItem: "home",
    examples: [
      "Where is Ask Noticeboard?",
      "Where's Ask Noticeboard?",
      "How do I open Ask Noticeboard?",
      "I can't find Ask Noticeboard",
      "Where is the sparkle?",
      "Where's the helper?",
      "How do I open the chat?",
      "Where is the Ask Noticeboard button?",
      "How do I open the assistant?",
      "Where do I find the sparkle button?",
      "Where do I find you again?",
      "Which lesson is Ask Noticeboard?",
      "Ok. How do I open you again?",
      "How do I open you again?",
      "Can you remember this conversation tomorrow?",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        (_v, _s, asked = "") =>
          /remember/i.test(asked)
            ? "This chat doesn't keep a permanent memory. Each question is matched on its own, with only a short follow-up."
            : "Press the sparkle next to your picture to open Ask Noticeboard, or use ⌘K on a Mac or Ctrl+K on Windows. On a phone, the sparkle is at the top right.",
        (_v, _s, asked = "") =>
          /remember/i.test(asked)
            ? "No. I don't remember this conversation tomorrow. Each question is matched on its own."
            : "Ask Noticeboard is the sparkle button in the top bar, beside your profile picture. You can also press ⌘K or Ctrl+K.",
        (_v, _s, asked = "") =>
          /remember/i.test(asked)
            ? "I can't promise to remember this chat tomorrow. Ask the question again and I'll match it."
            : "It's the sparkle next to your picture. Press it, or use Ctrl+K (⌘K on a Mac), to open Ask Noticeboard.",
      ],
      link: () => ({ label: "Ask Noticeboard", href: `${COURSE_PATH}/ask-noticeboard` }),
    },
  }),

  // "A Creator isn't told about pages they can't open": the facts hold for every role,
  // but the button only ever points someone to a page their own role can open.
  defineEntry<{ canOpenTeam: boolean }>({
    id: "concepts.creatorVsOwner",
    sidebarItem: "home",
    examples: [
      "What's the difference between a Creator and an Owner?",
      "What's the difference between an Owner and an Admin?",
      "How is a Creator different from an Owner?",
      "What can an Owner do that a Creator can't?",
      "What can an Admin do that an Owner can't?",
      "Creator vs Owner, what's the difference?",
      "What roles can I give them?",
      "Owner vs Admin, what's the difference?",
      "What's special about being an Admin?",
      "Are Owner and Admin the same thing?",
      "creator vs owner",
      "what can an admin do",
      "who can add staff",
      "Who should I ask?",
      "I'll ask an Admin if I'm not one",
      "Can a Creator open Ad Center?",
    ],
    answer: {
      load: async (_, ctx) => ({ canOpenTeam: canOpenSidebarItem(teamPage, ctx.user) }),
      templates: [
        ({ canOpenTeam }) =>
          `A Creator mainly writes Notices, and can't open Users, Circles, Team or Ad Center. An Owner can do everything a Creator can, plus manage Circles and Team — though an Owner can only add new Creators, not Owners or Admins. An Admin can do all of that, plus Users and Ad Center.${canOpenTeam ? "" : " Your own role is shown on Account."}`,
        ({ canOpenTeam }) =>
          `On the Team: a Creator writes Notices but can't open Users, Circles, Team or Ad Center; an Owner adds that plus Circles and Team, though only as far as adding Creators; an Admin can do everything, including Users and Ad Center.${canOpenTeam ? "" : " Check Account for your own role."}`,
        ({ canOpenTeam }) =>
          `Creator, Owner and Admin go in that order of access: a Creator just writes Notices; an Owner also manages Circles and Team (adding only Creators); an Admin has every page, including Users and Ad Center.${canOpenTeam ? "" : " Account shows which one you are."}`,
      ],
      link: (_, { canOpenTeam }) => (canOpenTeam ? openTeam() : openHome()),
    },
  }),

  defineEntry<{ canOpenCircles: boolean }>({
    id: "concepts.circle",
    sidebarItem: "home",
    examples: [
      "What is a Circle?",
      "What's a Circle?",
      "What does Circle mean?",
      "What's the point of a Circle?",
      "How do Circles work?",
      "What's a Circle used for?",
      "Can you explain what a Circle is?",
      "What's the difference between a Circle and a class?",
      "Is a Circle the same as a group?",
      "What are Circles for?",
      "whats a circle",
      "is a circle a class",
      "what are groups called here",
      "I don't understand a Circle",
      "whats a circle one more time",
      "Explain a Circle please",
    ],
    answer: {
      load: async (_, ctx) => ({ canOpenCircles: canOpenSidebarItem(circlesPage, ctx.user) }),
      templates: [
        ({ canOpenCircles }) =>
          `A Circle is a named group inside your Organization, like a class. Notices and Events can be sent to a Circle instead of everyone.${canOpenCircles ? "" : " Circles is for an Owner or Admin; a Creator can't open it."}`,
        ({ canOpenCircles }) =>
          `It's a group you set up inside your Organization — a class, a team, anything you like — so a Notice or Event can go to just that group.${canOpenCircles ? "" : " An Owner or Admin opens Circles; a Creator can't."}`,
        ({ canOpenCircles }) =>
          `Circles are your Organization's own groups, like Grade 2 or Youth, used to send a Notice or Event to just the right people.${canOpenCircles ? "" : " You can't open Circles as a Creator."}`,
      ],
      link: (_, { canOpenCircles }) => (canOpenCircles ? openCircles() : openHome()),
    },
  }),

  defineEntry<{ canOpenCircles: boolean }>({
    id: "concepts.createCircle",
    sidebarItem: "home",
    examples: [
      "How do I create a Circle?",
      "Can a Creator manage circles?",
      "How do I make a new Circle?",
      "Where is the add circle button?",
      "How do I start a new class group?",
      "Where can I make a new group?",
      "How do I add a Circle?",
      "I need a group for parents",
      "How do I create a class group?",
      "Where do I press Create Circle?",
      "Can I make a Circle?",
      "am i allowed to manage groups",
      "How do I create one?",
      "change circle name",
      "Which page is that button on?",
      "Is a Circle the same as the organization name?",
      "I still can't see Circles in the sidebar",
      "Where is the circle button?",
      ...askedHow("create a circle"),
      ...askedHow("make a circle"),
      ...askedHow("create the class circle"),
    ],
    answer: {
      load: async (_, ctx) => ({ canOpenCircles: canOpenSidebarItem(circlesPage, ctx.user) }),
      templates: [
        ({ canOpenCircles }, _slots, asked = "") =>
          /\bcreator\b/i.test(asked)
            ? "No. A Creator can't open Circles. An Owner or Admin presses CREATE CIRCLE."
            : /\borgani[sz]ation name\b/i.test(asked)
              ? "No. The Circle name and the organization name on Account are different."
              : canOpenCircles
              ? "On Circles, press CREATE CIRCLE, fill in the Circle Name and Description, then press Create Sub-Circle."
              : /\b(?:rename|name)\b/i.test(asked)
                ? "No. A Circle's name is separate from the Organization name on Account. A Creator can't open Circles. An Owner or Admin changes the Circle's name."
                : "No. A Creator can't open Circles. An Owner or Admin presses CREATE CIRCLE.",
        ({ canOpenCircles }, _slots, asked = "") =>
          /\bcreator\b/i.test(asked)
            ? "No. A Creator can't manage Circles. An Owner or Admin creates one."
            : /\borgani[sz]ation name\b/i.test(asked)
              ? "No. The Circle name and the organization name on Account are different."
              : canOpenCircles
              ? "Open Circles and press CREATE CIRCLE, or Add Your First Circle if you have none yet."
              : /\b(?:rename|name)\b/i.test(asked)
                ? "No. Renaming a Circle is for an Owner or Admin. The Organization name on Account is a different name, and a Creator can't change either."
                : "No. Circles aren't in a Creator's sidebar. An Owner or Admin creates a Circle there.",
        ({ canOpenCircles }, _slots, asked = "") =>
          /\bcreator\b/i.test(asked)
            ? "No. A Creator can't manage Circles. Ask an Owner or Admin to press CREATE CIRCLE."
            : /\borgani[sz]ation name\b/i.test(asked)
              ? "No. The Circle name and the organization name on Account are different."
              : canOpenCircles
              ? "Circles in the sidebar is where you press CREATE CIRCLE."
              : /\b(?:rename|name)\b/i.test(asked)
                ? "No. A Creator can't open Circles, so they can't rename one. The Circle's name is not the Organization name on Account."
                : "No. A Creator can't manage Circles. Ask an Owner or Admin to press CREATE CIRCLE.",
      ],
      link: (_, { canOpenCircles }) => (canOpenCircles ? openCircles() : openHome()),
    },
  }),

  defineEntry<{ canOpenCircles: boolean; self: boolean }>({
    id: "concepts.addToCircle",
    sidebarItem: "home",
    examples: [
      "How do I add someone to a Circle?",
      "How do I add a parent to a Circle?",
      "How do I add a parent to my class Circle?",
      "How do I add a parent to my class?",
      "How do I put a parent in a class group?",
      "How do I add a member to a Circle?",
      "How do I give someone access to a Circle?",
      "Can I add a User to a Circle?",
      "How do I add people to a group?",
      "Where do I add a member?",
      "How do I put someone in a Circle?",
      "Can I add a parent to Grade 2?",
      "put a parent in the class group",
      "I can't add a parent to it?",
      "How do I add a parent to it?",
      ...askedHow("add a parent to that circle"),
      ...askedHow("add that parent to a circle after they join"),
      "How do I put one parent in the circle without messaging everyone?",
    ],
    answer: {
      load: async ({user}, ctx) => ({ canOpenCircles: canOpenSidebarItem(circlesPage, ctx.user), self:user === "myself" }),
      templates: [
        ({ canOpenCircles, self }) =>
          self ? "Ask an Owner or Admin to give you access to the Circle. You have not been added by this chat." : canOpenCircles
            ? "On Circles, open the Circle and press Add Users, then add the parent. They need to have joined first; someone new is invited on Users, where you can choose their Circles."
            : "Adding someone to a Circle is for an Owner or Admin. A Creator can't open Circles, and nobody is added from here. Inviting a new parent to Noticeboard is a separate Admin task.",
        ({ canOpenCircles, self }) =>
          self ? "Ask an Owner or Admin to give you access to the Circle. You have not been added by this chat." : canOpenCircles
            ? "Open the Circle on Circles, press Add Users and pick the parent. Someone who hasn't joined yet is invited on Users, with their Circles chosen there."
            : "You can't open Circles, so you can't add someone. Ask an Owner or Admin.",
        ({ canOpenCircles, self }) =>
          self ? "Ask an Owner or Admin to give you access to the Circle. You have not been added by this chat." : canOpenCircles
            ? "Press Add Users on that Circle's page, under Circles, and pick the parent. A parent who hasn't joined is invited on Users first."
            : "A Creator doesn't have Circles in the sidebar. An Owner or Admin adds the person. They have not been added.",
      ],
      link: (_, { canOpenCircles }) => (canOpenCircles ? openCircles() : openHome()),
    },
  }),

  defineEntry({
    id: "concepts.subcircle",
    sidebarItem: "home",
    examples: [
      "What is a sub-circle?",
      "What is a subcircle?",
      "How do I make a subcircle?",
      "Where are sub-circles?",
      "Add your subcircle, what does that mean?",
      "Is a sub circle a class?",
      "What's the difference between a Circle and a sub-circle?",
      "Is there a sub-circles page?",
      "What does subcircle mean?",
      "Do we have sub-circles?",
      "The checklist says add your subcircle",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () =>
          "Circle is the name in the sidebar. Account's Getting Started checklist says Add your subcircle, which means your first Circle. There isn't a separate Sub-circles page.",
        () =>
          "A subcircle is a Circle. The button that creates one says Create Sub-Circle. There is no separate Sub-circles page.",
        () =>
          "Subcircle and Circle are the same thing here. Getting Started calls the first one Add your subcircle.",
      ],
      link: openHome,
    },
  }),

  defineEntry<{ canOpenCircles: boolean }>({
    id: "concepts.whoSeesCircle",
    sidebarItem: "home",
    examples: [
      "Who can see a Circle?",
      "Are Circles private?",
      "Who sees a class group?",
      "Can other classes see this Circle?",
      "Who receives a Circle's notices?",
      "Circle visibility",
      "Who is a Circle visible to?",
      "Do other Circles see this one?",
      "Who can view a Circle?",
      "What do members of a Circle see?",
    ],
    answer: {
      load: async (_, ctx) => ({ canOpenCircles: canOpenSidebarItem(circlesPage, ctx.user) }),
      templates: [
        ({ canOpenCircles }) =>
          `People in a Circle see the Notices sent to that Circle.${canOpenCircles ? " You manage who's in it on Circles." : " An Owner or Admin manages who's in it. A Creator can't open Circles."}`,
        ({ canOpenCircles }) =>
          `A Circle's members receive what is sent to that Circle, not to other Circles.${canOpenCircles ? "" : " You can't open the member list as a Creator."}`,
        ({ canOpenCircles }) =>
          `Only the people in the Circle see Notices sent to it.${canOpenCircles ? "" : " Membership is for an Owner or Admin, not a Creator."}`,
      ],
      link: (_, { canOpenCircles }) => (canOpenCircles ? openCircles() : openHome()),
    },
  }),

  defineEntry<{ role: string }>({
    id: "concepts.myRole",
    sidebarItem: "home",
    examples: [
      "What is my role?",
      "Am I an admin?",
      "What permissions do I have?",
      "What's my access level?",
      "Am I a teacher or an admin?",
      "What role am I?",
      "Which role am I?",
      "Which role do I have?",
      "Can I change my own role to Owner?",
      "Am I a Creator?",
      "What can I do with my role?",
      "Tell me my role",
      "Who am I signed in as?",
    ],
    answer: {
      load: async (_, ctx) => ({ role: ctx.user?.userType === "team" ? String(ctx.user.accessLevel || "Creator") : "Admin" }),
      templates: [
        ({ role }) =>
          `You're ${aRole(role)}. A Creator writes Notices and can't open Users, Circles, Team or Ad Center. An Owner can also manage Circles and Team. An Admin can also open Users and Ad Center.`,
        ({ role }) =>
          `Your role is ${role}. Creators write Notices. Owners also manage Circles and Team. Admins also open Users and Ad Center. Account shows your own role.`,
        ({ role }) =>
          `You're ${aRole(role)} on the Team. A Creator can't open Users, Circles, Team or Ad Center. An Owner or Admin can manage Team roles within their permissions.`,
      ],
      link: openHome,
    },
  }),

  defineEntry<{ canOpen: boolean }>({
    id: "concepts.teamMember",
    sidebarItem: "home",
    examples: [
      "What is a team member?",
      "What's a team member?",
      "but what is a team member?",
      "What does team member mean?",
      "Who counts as a team member?",
      "Tell me about a Team member",
      "Explain a Team member please",
      "What is a team?",
      "What's a team?",
      "what is a team",
      "no i want to know what a team is",
      "What's the difference between a user and a team member?",
      "What's the difference with a user and a team member?",
      "whats the difference with a user and a team member?",
      "a staff invite is Team",
    ],
    answer: {
      load: async (_, ctx) => ({ canOpen: canOpenSidebarItem(teamPage, ctx.user) }),
      templates: [
        ({ canOpen }) =>
          `A team member uses this dashboard: an Admin, an Owner or a Creator. A User is different — that's who receives Notices in the app, usually a parent.${canOpen ? "" : " Team is for an Owner or Admin."}`,
        ({ canOpen }) =>
          `Team members are the people signed in here. Users are the people who get Notices in the app.${canOpen ? " You see the Team on the Team page." : " An Owner or Admin opens Team."}`,
        ({ canOpen }) =>
          `A team member works in the dashboard. A User gets the Notices, usually a parent.${canOpen ? "" : " You can't open Team as a Creator."}`,
      ],
      link: (_, { canOpen }) => (canOpen ? openTeam() : openHome()),
    },
  }),

  defineEntry<{ canOpen: boolean }>({
    id: "concepts.user",
    sidebarItem: "home",
    examples: [
      "What is a user?",
      "What's a user?",
      "what is a user",
      "What does User mean?",
      "Who is a User?",
      "I don't understand a User",
      "Tell me about a User",
      "Members, not parents. Same thing?",
      "Is a parent a team member?",
      "a parent invite is Users, not Team",
    ],
    answer: {
      load: async (_, ctx) => ({ canOpen: canOpenSidebarItem(usersPage, ctx.user) }),
      templates: [
        ({ canOpen }) =>
          `A User receives Notices in the app, usually a parent. A team member is someone using this dashboard.${canOpen ? "" : " Users is for an Admin."}`,
        ({ canOpen }) =>
          `Users are the people the Notices go to. Team members are the people who send them from here.${canOpen ? "" : " An Admin opens Users. A Creator can't."}`,
        ({ canOpen }) =>
          `A User is in the app. A team member is on this dashboard.${canOpen ? "" : " You can't open Users as a Creator."}`,
      ],
      link: (_, { canOpen }) => (canOpen ? openUsers() : openHome()),
    },
  }),

  defineEntry({
    id: "concepts.guardian",
    sidebarItem: "home",
    examples: [
      "What is a guardian?",
      "What's a guardian?",
      "whats a guardian",
      "Can you explain a guardian?",
      "I don't understand a guardian",
      "Tell me about a guardian",
      "What does a guardian mean here?",
      "Explain a guardian please",
      "What does guardian mean on Noticeboard?",
      "Is a guardian the same as a parent?",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () =>
          "A guardian is a parent or another family adult linked to a User, usually the person who receives that child's Notices.",
        () =>
          "A guardian is the family adult on a User's account, such as a parent. This chat doesn't list a child's guardians.",
        () =>
          "Guardians are the parents or family adults linked to a User. Looking up one child's family is a separate Users search.",
      ],
      link: openHome,
    },
  }),

  defineEntry<{ canOpenUsers: boolean }>({
    id: "concepts.inviteParent",
    sidebarItem: "home",
    examples: [
      "How do I invite a parent?",
      "How do I get parents to join?",
      "How do I get parents onto the app?",
      "Can a Creator invite parents?",
      "How do I add a parent?",
      "How do I invite users?",
      "How do I add a mom?",
      "How do I send an invite?",
      "How do I invite someone to the app?",
      "Can I invite parents?",
      "Where do I invite a User?",
      "How do I add a new parent?",
      "How do parents get invited?",
      "can i invite if im a creator",
      "add a parent",
      "send an invite",
      ...askedHow("invite a parent"),
      "Can I add a parent who is not on Noticeboard yet?",
      ...askedHow("invite a member"),
      "Add user is the confirm button for an invite",
      "I'm a Creator in this test. Why can't I see Users?",
    ],
    answer: {
      load: async (_, ctx) => ({ canOpenUsers: canOpenSidebarItem(usersPage, ctx.user) }),
      templates: [
        ({ canOpenUsers }, _slots, asked = "") =>
          /\bcreator\b/i.test(asked)
            ? "No. A Creator can't open Users. An Owner or Admin invites a parent."
            : canOpenUsers
              ? "On Users, press ADD USER, enter their email or phone number, and press Add user. Nothing is sent until you do that."
              : "Inviting a parent is for an Admin, on Users. A Creator can't open Users, and no invite is sent from here.",
        ({ canOpenUsers }, _slots, asked = "") =>
          /\bcreator\b/i.test(asked)
            ? "No. A Creator can't invite parents. An Owner or Admin does that on Users."
            : canOpenUsers
              ? "Open Users, press ADD USER, add their email or phone number and choose their Circles, then press Add user."
              : "You can't invite parents. That's an Admin task on Users, which isn't in your sidebar.",
        ({ canOpenUsers }, _slots, asked = "") =>
          /\bcreator\b/i.test(asked)
            ? "No. A Creator can't open Users, so they can't invite parents. Ask an Owner or Admin."
            : canOpenUsers
              ? "On Users, press ADD USER, enter the parent's email or phone number, and press Add user. Nothing is sent until then."
              : "A Creator doesn't have Users. Ask an Admin to send the invite.",
      ],
      link: (_, { canOpenUsers }) => (canOpenUsers ? openUsers() : openHome()),
    },
  }),

  defineEntry<{ canOpenTeam: boolean }>({
    id: "concepts.addTeacher",
    sidebarItem: "home",
    examples: [
      "How do I add a teacher to the team?",
      "How do I add a staff member?",
      "How do I add a teacher?",
      "Where is Team?",
      "How do I add someone to the dashboard?",
      "How do I give a teacher a login?",
      "Can I add a Team member?",
      "How do I add a new teacher?",
      "Where do I press ADD MEMBER?",
      "How do I invite a colleague to the Team?",
      "add a staff member",
      ...askedHow("add a teacher to the team"),
      ...askedHow("invite the class teacher onto the team"),
    ],
    answer: {
      load: async (_, ctx) => ({ canOpenTeam: canOpenSidebarItem(teamPage, ctx.user) }),
      templates: [
        ({ canOpenTeam }) =>
          canOpenTeam
            ? "On Team, press ADD MEMBER, fill in their name and phone number, choose their role, and press Send invite. Nothing is sent until you do that."
            : "Adding a teacher is for an Owner or Admin, on Team. A Creator can't open Team, and nobody is added from here.",
        ({ canOpenTeam }) =>
          canOpenTeam
            ? "Team is where an Owner or Admin presses ADD MEMBER."
            : "You can't open Team. An Owner or Admin adds teachers. They have not been added.",
        ({ canOpenTeam }) =>
          canOpenTeam
            ? "Open Team and press ADD MEMBER."
            : "A Creator doesn't have Team in the sidebar. Ask an Owner or Admin.",
      ],
      link: (_, { canOpenTeam }) => (canOpenTeam ? openTeam() : openHome()),
    },
  }),

  defineEntry({
    id: "concepts.gettingStarted",
    sidebarItem: "home",
    examples: [
      "What should I do first?",
      "I'm new, where do I start?",
      "Getting started",
      "What do I set up first?",
      "Where do I begin?",
      "What is the first thing I should do?",
      "How do I get started?",
      "What's on the Getting Started checklist?",
      "I'm new here, what now?",
      "Where should a new teacher start?",
      "im new where do i start",
      "onboarding",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () =>
          "On Account, Getting Started lists add your first user, create your first notice, and add your subcircle. You can create a Notice. Adding users and Circles is for an Admin or Owner; a Creator can't open those pages.",
        () =>
          "Start by creating a Notice. The Getting Started checklist also mentions a user and a subcircle, which a Creator can't do.",
        () =>
          "Create your first Notice. The other Getting Started items, a user and a Circle, aren't in a Creator's sidebar.",
      ],
      link: openHome,
    },
  }),

  defineEntry({
    id: "concepts.whereIsThat",
    sidebarItem: "home",
    examples: [
      "I don't see it. Where is that?",
      "I don't see it",
      "Where is that?",
      "I still can't find it",
      "That didn't work",
      "It isn't there",
      "I can't see it",
      "Where did that go?",
      "I don't see what you mean",
      "Point me to it again",
      "I'm stuck on the screen",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () => "Tell me which page or button you can't see, and I'll point you to it.",
        () => "Which part can't you see? Name the page or the button and I'll show you where it is.",
        () => "I need to know what you're looking for. Which page or button is missing?",
      ],
      link: openHome,
    },
  }),

  defineEntry({
    id: "concepts.homeNumbers",
    sidebarItem: "home",
    examples: [
      "What do the numbers on Home mean?",
      "Explain the dashboard numbers",
      "What is Total Notices?",
      "Why does Home say one event?",
      "What are the Home cards?",
      "What does All Users mean?",
      "What does Active Adverts mean?",
      "What do the Home totals mean?",
      "Explain the numbers on the overview",
      "What is Upcoming Events on Home?",
      "What's on the home page numbers?",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () =>
          "Home shows All Users, Total Notices, Upcoming Events and Active Adverts. Total Notices is the notice-count on Home. I can't say which statuses it includes, so it can differ from the Sent list. Upcoming Events on Home is not the same list as the Calendar.",
        () =>
          "The four cards are All Users, Total Notices, Upcoming Events and Active Adverts. Total Notices can use a different scope from Notices → Sent. Home's Upcoming Events and the Calendar list are different counts.",
        () =>
          "All Users, Total Notices, Upcoming Events and Active Adverts are the Home cards. I don't have the date range behind Upcoming Events, which is why it can differ from the Calendar.",
      ],
      link: openHome,
    },
  }),

  defineEntry({
    id: "concepts.activeAdverts",
    sidebarItem: "home",
    examples: [
      "How many active adverts do we have?",
      "How many active adverts?",
      "Any ads running?",
      "What's the advert count?",
      "advert count",
      "Active adverts",
      "Are any adverts running?",
      "How many ads are active?",
      "Do we have active adverts?",
      "What's the Active Adverts number?",
      "Are there any adverts?",
      "What are active adverts?",
      "How are our ads doing?",
      "How are the adverts doing?",
    ],
    answer: {
      // Counted as Home's Active Adverts card counts them
      load: async (_, { user, token, queryClient }) => {
        const ads = await queryClient.fetchQuery(viewQuery(user, advertisementsQuery(user?.schoolId, token)));
        const now = new Date();
        return {
          active: ads.filter((ad) => !ad.isDraft && !ad.isDeleted && isAdActive(ad, now)).length,
          canOpen: canOpenSidebarItem(adCenterPage, user),
        };
      },
      templates: [
        ({ active, canOpen }) => `Home shows ${activeAdverts(active)}. ${canOpen ? "Ad Center has each one's details." : "Only an Admin can open Ad Center."}`,
        ({ active, canOpen }) => `The Active Adverts card on Home shows ${activeAdverts(active)}. ${canOpen ? "Open Ad Center to see each one." : "Ad Center is for an Admin."}`,
        ({ active, canOpen }) => `There ${active === 1 ? "is" : "are"} ${activeAdverts(active)} on Home. ${canOpen ? "Ad Center lists them." : "Only an Admin can open Ad Center."}`,
      ],
      link: (_, { canOpen }) => (canOpen ? { label: `Open ${adCenterPage.label}`, href: adCenterPage.path } : openHome()),
    },
  }),

  defineEntry({
    id: "concepts.adCenter",
    sidebarItem: "home",
    examples: [
      "How do I set up the Ad Center?",
      "Where is Ad Center?",
      "How do I set up ads?",
      "How do we earn from ads?",
      "Can I open Ad Center?",
      "Where are the adverts set up?",
      "How do I start Ad Center?",
      "Is Ad Center available to me?",
      "How do I configure ads?",
      "Set up your ad center",
      "What is Ad Center for?",
      "Can I put an advert in a notice?",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () => "Ad Center isn't in your sidebar. Only an Admin can open it. A Creator can't set it up from here.",
        () => "Home may mention Ad Center, but a Creator can't open it. Ask an Admin.",
        () => "You can't open Ad Center. That's an Admin page, and it isn't in your sidebar.",
      ],
      link: openHome,
    },
  }),

  defineEntry({
    id: "concepts.feedIsNotices",
    sidebarItem: "home",
    examples: [
      "The menu says Feed. Is that Notices?",
      "Where is the feed?",
      "Is Feed the same as Notices?",
      "I see Feed not Notices",
      "Feed vs Notices",
      "Why does the menu say Feed?",
      "Is the feed the notices page?",
      "Does Feed mean Notices?",
      "Where did Notices go on my phone?",
      "The shortcut says Feed",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () => "On a phone the shortcut says Feed. On the desktop sidebar that same page is Notices. They are the same area.",
        () => "Feed and Notices are the same page. The narrow screen says Feed; the desktop sidebar says Notices.",
        () => "Yes. Feed is Notices. The phone menu uses Feed for the Notices page.",
      ],
      link: openNotices,
    },
  }),

  defineEntry({
    id: "concepts.circlesTile",
    sidebarItem: "home",
    examples: [
      "Home shows a Circles tile. Can I open it?",
      "I clicked Circles and nothing happened",
      "Why is Circles on the Home page?",
      "Can I open the Circles tile?",
      "The Home page has a Circles tile",
      "Does the Circles tile open Circles?",
      "Why can't I open Circles from Home?",
      "Home has Circles but I can't find the page",
      "Is the Circles tile for me?",
      "What does the Circles tile do?",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () => "Home shows a Circles tile, but a Creator can't open Circles. It isn't in your sidebar, and opening it returns you to Home.",
        () => "The tile is on Home. Circles itself is for an Owner or Admin, so a Creator can't manage Circles from it.",
        () => "You can't open Circles. The Home tile doesn't put Circles in a Creator's sidebar.",
      ],
      link: openHome,
    },
  }),

  defineEntry({
    id: "concepts.addUsersTile",
    sidebarItem: "home",
    examples: [
      "Home shows Add Users. What happens if I use it?",
      "What does the Add Users tile do?",
      "Why is Add Users on the Home page?",
      "The Home button for inviting",
      "Can I use Add Users?",
      "What happens when I press Add Users?",
      "Add Users is on Home",
      "Does Add Users work for a Creator?",
      "I see Add Users on Home",
      "Where does Add Users go?",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () => "Home shows Add Users, but Users is for an Admin. A Creator can't open it, and no invite is sent.",
        () => "The Add Users tile is on Home. Inviting people is an Admin task. A Creator can't open Users.",
        () => "Pressing it doesn't invite anyone for a Creator. Users isn't in your sidebar; an Admin invites parents.",
      ],
      link: openHome,
    },
  }),

  defineEntry({
    id: "concepts.tenSecondNotice",
    sidebarItem: "home",
    examples: [
      "How do I send a notice in about 10 seconds?",
      "How do I send a notice in 10 seconds?",
      "Send a notice in 10s",
      "Where is the 10 second notice?",
      "How do I send a quick notice?",
      "The 10 second notice",
      "How do I use Send a notice in 10s?",
      "What's the quick notice button?",
      "How do I send a notice quickly from Home?",
      "Where is Send a notice in 10s?",
      "quick notice",
      "The home page says 10 seconds. How?",
      "Show me the ten second version of sending a notice",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () => "Home's quick action is Send a notice, with the line Send a notice in 10s. It opens Create. You still review it and press Publish before it goes out.",
        () => "Press Send a notice in 10s on Home. That opens Create, and nothing is sent until you review it and press Publish.",
        () => "The 10-second Notice is the Send a notice button on Home. It starts Create; you still check it and press Publish yourself.",
      ],
      link: openCreate,
    },
  }),

  defineEntry({
    id: "concepts.stagingBadge",
    sidebarItem: "home",
    examples: [
      "What does the STAGING badge mean?",
      "Why does it say staging?",
      "Are parents seeing staging?",
      "What is the red STAGING badge?",
      "Does the staging badge show to parents?",
      "Why is there a staging label?",
      "What does staging mean on the dashboard?",
      "Is this the staging environment?",
      "Who sees the STAGING badge?",
      "The header says STAGING",
      "Is this the live school?",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () => "The red STAGING badge means this dashboard session is on the staging environment. It is not a label parents see in the app.",
        () => "STAGING marks this as the staging environment, for people using this dashboard. Parents don't see that badge.",
        () => "You're on staging. The red badge is for this dashboard session, not a parent-facing banner.",
      ],
      link: openHome,
    },
  }),

  defineEntry({
    id: "concepts.circleVideo",
    sidebarItem: "home",
    examples: [
      "There is a video about creating a Circle. Can I follow it?",
      "The help video says create a Circle",
      "Can I follow the Circle video?",
      "There's a tutorial for Circles but I have no button",
      "Can I do what the Create a Circle video says?",
      "Should I follow the Circle tutorial?",
      "The video shows how to create a Circle",
      "Can a Creator follow the Circle video?",
      "I watched How to Create a Circle",
      "Does the Circle video apply to me?",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () => "Help Center has a video, How to Create a Circle. You can watch it. Creating a Circle is for an Owner or Admin; a Creator can't open Circles.",
        () => "Watching the Circle video is fine. Doing it needs Circles, which a Creator can't open. An Owner or Admin can.",
        () => "The video is in Help Center. A Creator can't open Circles, so an Owner or Admin has to create the Circle.",
      ],
      link: () => ({ label: "Open Video Tutorials", href: "/help/videos" }),
    },
  }),

  defineEntry({
    id: "concepts.signature",
    sidebarItem: "create",
    examples: [
      "What is a signature on Noticeboard?",
      "Where do I manage certificate signatures?",
      "Where are signatures?",
      "Where do I add signatures?",
      "What is a signature?",
      "Who signs certificates?",
      "Add a principal signature",
      "What does Add signatures mean?",
      "How do signatures work?",
      "Is a signature a person's name?",
      "What is a signature used for?",
      "Where do I add someone who signs certificates?",
      "Can I do a signature?",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () => "A signature is a person who signs on your Organization's behalf, such as a principal. Add signatures is on Create, next to Certificates. Editing one doesn't rewrite certificates already sent.",
        () => "Signatures are the names and titles that appear on certificates. You add them on Create. Changing one later doesn't change certificates already sent.",
        () => "It's the person who signs a certificate. On Create, press Add signatures. Already-sent certificates stay as they were.",
      ],
      link: openCreate,
    },
  }),
];

export const conceptsCreateEntries = [
  defineEntry({
    id: "concepts.doesNotSend",
    sidebarItem: "create",
    examples: [
      "Can you publish a notice yourself?",
      "Will you send it without me checking?",
      "Do you send notices for me?",
      "Can you send it on your own?",
      "Can you publish the notice yourself?",
      "Will you post this without me?",
      "Can you send the notice without asking?",
      "Do you send it, or do I?",
      "Can you just send the notice?",
      "Will you publish it without me?",
      "Don't send it.",
      "And you still haven't sent anything, right?",
      "Did you already put it on the calendar?",
      "Did you add them already?",
      "So, to be clear, you have not invited anyone?",
      "Did you already send it?",
      "Did anything get sent?",
      "Was anything sent?",
      "Did that send?",
      "Did you publish the poll?",
      "Did you send the closure?",
      "Did you already send the certificate?",
      "Is this chat going to email the certificate?",
      "And a normal notice is Publish?",
      "a normal notice is Publish?",
      "Good, I won't press anything until I've checked the names",
      "did u send it",
      "publish it",
      "you still have not published anything",
      "You haven't added my new event, have you?",
      "Will you publish if I say yes?",
      "You still won't send it, right?",
      "What can you not do?",
      "What can't you do?",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        (_values, _slots, asked = "") =>
          /calendar/i.test(asked)
            ? "No. I haven't put it on the Calendar. I can prepare the Notice on Create. You still check it and press Publish — I don't send it on my own."
            : "No. I haven't sent anything. You review it and press Publish. Nothing is sent until then.",
        (_values, _slots, asked = "") =>
          /calendar/i.test(asked)
            ? "No. It isn't on the Calendar. You add it on Create, then you review it and press Publish."
            : "No. Nothing has gone out from here. I can fill in Create for you, but I don't send it. You review it and press Publish.",
        (_values, _slots, asked = "") =>
          /calendar/i.test(asked)
            ? "No. I haven't put anything on the Calendar. Nothing appears there until you press Publish."
            : "No. I haven't sent it. I'll help you write it, then you review it. Nothing is sent until you press Publish.",
      ],
      link: openCreate,
    },
  }),

  // What's Nova? It denied its own feature in the 5 Oct QA run (H8).
  defineEntry({
    id: "concepts.nova",
    sidebarItem: "create",
    examples: [
      "What's Nova?",
      "What is Nova?",
      "Who or what is Nova?",
      "who is nova",
      "What does Nova do?",
      "Can Nova rewrite my notice?",
      "What is Rewrite with Nova?",
      "Tell me about Nova",
      "Is Nova an AI?",
      "What's the Nova button for?",
      "Does Noticeboard have an AI helper?",
      "can nova write my notice",
      "I don't understand Nova",
      "What does Nova mean here?",
      "Explain Nova please",
      "whats Nova",
      "Are you Nova?",
      "Can Nova write the certificate wording?",
      "And Nova",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        (_values, _slots, asked = "") =>
          `${novaIntro(asked)}Nova is part of Noticeboard: in Create, press Rewrite with Nova on a Notice to get a better-worded version of what you've written.`,
        (_values, _slots, asked = "") =>
          `${novaIntro(asked)}Nova is Noticeboard's own writing help, built into Create. Write your Notice, then press Rewrite with Nova to improve the wording.`,
        (_values, _slots, asked = "") =>
          `${novaIntro(asked)}It's the Rewrite with Nova button in Create — it reworks the Notice text you've already written.`,
      ],
      link: openCreate,
    },
  }),

  // "Tell me about certificates" was starting New Award and asking for a title.
  defineEntry({
    id: "concepts.certificates",
    sidebarItem: "create",
    examples: [
      "Tell me about certificates",
      "Open certificates",
      "Where are certificates?",
      "Take me to certificates",
      "I want to look at certificates",
      "Show me the certificates",
      "View certificates",
      "What are certificates?",
      "What is a certificate?",
      "What is an Award?",
      "What's an Award?",
      "whats an Award",
      "Can you explain an Award?",
      "I don't understand an Award",
      "Tell me about an Award",
      "What does an Award mean here?",
      "Explain an Award please",
      "How do certificates work?",
      "Explain certificates",
      "Tell me about Awards",
      "What are Awards for?",
      "How do Awards work?",
      "What's the difference between an Award and a certificate?",
      "How are certificates sent?",
      "What does an Award do?",
      "Can you explain certificates?",
      "I don't understand a certificate",
      "where are certificates",
      "certificates",
      "certificate",
      "Send a certificate to a parent",
      "Can I send it to one parent?",
      "Who is allowed to make awards?",
      "Can I print a stack of certificates from here?",
      "I don't see certificates",
      "Can an Award be a Notice as well?",
      "Is a certificate the same as a notice template?",
      "Can the course give me a certificate?",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () =>
          "Certificates are made as an Award. You choose a published certificate template, add the Awardees, and each person's certificate is sent to their Users as a Notice.",
        () =>
          "An Award is one use of a certificate template for a list of Awardees, such as Maths Olympics – Gold. Each Awardee gets their own certificate, sent to their Users.",
        () =>
          "Open Certificates from Create. An Award names the certificate template and the Awardees, and sending it delivers each certificate to that Awardee's Users.",
      ],
      link: openCertificates,
    },
  }),
];

export const conceptsNoticesEntries = [
  // Notice vs Event vs Information
  defineEntry({
    id: "concepts.noticeVsEvent",
    sidebarItem: "notices",
    alsoNeeds: ["calendar"],
    examples: [
      "What is a Notice?",
      "What is an Event?",
      "What's the difference between a notice and an event?",
      "How is an Event different from a Notice?",
      "What's an Information notice?",
      "Is an Event a kind of Notice?",
      "What's the difference between Information and an Event?",
      "What makes something an Event instead of a Notice?",
      "Why does an Event show on the Calendar?",
      "What kinds of Notices are there?",
      "notice vs event",
      "is an event a notice",
      "why are there both notices and events",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () =>
          "A Notice is the thing you send — Information is a Notice that's just text, with nothing to attend. An Event is a Notice with a date and time, so it also shows on the Calendar.",
        () =>
          "Notice is the general name for what you send. Information is a plain, read-only one; an Event has a date and time and appears on the Calendar too.",
        () =>
          "Every Event is a Notice, just one with a date and time attached, which is why it also shows on the Calendar. Information is a Notice with no date to attend.",
      ],
      link: openNotices,
    },
  }),

  // What will parents see when I send a notice?
  defineEntry({
    id: "concepts.whatParentsSeeOnNotice",
    sidebarItem: "notices",
    examples: [
      "What will parents see when I send a notice?",
      "What do parents see when I send a notice?",
      "What does a parent see when I send a notice?",
      "What does a parent see on a notice?",
      "What will Users see when I send a Notice?",
      "If I send a notice, what will parents see?",
      "What happens on the parent's side when I send a notice?",
      "What does sending a notice look like for parents?",
      "What do parents see on their end when I send a notice?",
      "How does a notice look to parents?",
      "What will families see once I send this?",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () =>
          "They get it in the Noticeboard app, sent to whichever Circle, User or Everyone you chose. An Event also shows on their calendar, and an Urgent Notice is flagged as urgent.",
        () =>
          "Whoever it's sent to — a Circle, a User or Everyone — gets it in the Noticeboard app. Events also appear on their calendar, and Urgent Notices are marked urgent.",
        () =>
          "It reaches the app for the Circle, User or Everyone you chose. If it's an Event it's on their calendar too, and Urgent Notices show as urgent.",
      ],
      link: openNotices,
    },
  }),

  // Can parents reply to a notice?
  defineEntry({
    id: "concepts.parentsCanReply",
    sidebarItem: "notices",
    examples: [
      "Can parents reply to a notice?",
      "Can parents reply to a Notice?",
      "Can a parent respond to a notice?",
      "Do parents get to answer a notice?",
      "Can parents write back on a notice?",
      "Is there a way for parents to reply?",
      "Can parents comment on a notice?",
      "How do parents respond to a notice?",
      "Can families reply to what I send?",
      "Do parents have a way to answer back?",
      "can moms comment",
      "do parents message back",
      "Can they reply to it?",
      "Can they reply to the closure notice?",
      "is there a reply button",
      "can they answer a notice",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () =>
          "Not with a written reply. On a Poll, parents answer with one tap; on an Event with RSVP turned on, they say yes, no or maybe.",
        () =>
          "A plain Notice doesn't take a reply. Use a Poll for a one-tap answer, or turn on RSVP on an Event for yes, no or maybe.",
        () =>
          "There's no free-text reply on a Notice. Parents can vote on a Poll with one tap, or RSVP yes, no or maybe on an Event.",
      ],
      link: openCreate,
    },
  }),

  // Creators can explain how parents receive Notices even though they cannot open Users.
  defineEntry<{ canOpenUsers: boolean }>({
    id: "concepts.parentsNeedApp",
    sidebarItem: "notices",
    examples: [
      "How do parents get our messages?",
      "How do parents get our notices?",
      "How do families see notices?",
      "Where do parents read this?",
      "Do parents need to download an app?",
      "Do parents need an app?",
      "Does a parent need to install anything?",
      "How do parents get Noticeboard?",
      "Do families need to download the app?",
      "What app do parents use?",
      "do moms get an email or what",
      "And how do parents actually receive it?",
      "Does the parent get it in the app?",
    ],
    answer: {
      load: async (_, ctx) => ({ canOpenUsers: canOpenSidebarItem(usersPage, ctx.user) }),
      templates: [
        ({ canOpenUsers }) =>
          canOpenUsers
            ? "Yes — Users get your Notices through the Noticeboard app. Once you invite them on Users, they join by downloading it and signing in. They don't get them as an SMS or WhatsApp."
            : "Parents get your Notices in the Noticeboard app, after an Admin invites them. Notices sent to their Circle, or to Everyone, show up there. They don't get them as an SMS or WhatsApp.",
        ({ canOpenUsers }) =>
          canOpenUsers
            ? "Yes, parents join through the Noticeboard app. You invite them from Users, and they download it to sign up. Notices are not sent by SMS or WhatsApp."
            : "Parents read Notices in the Noticeboard app. An Admin invites them, and anything sent to their Circle or to Everyone appears there, not as an SMS or WhatsApp.",
        ({ canOpenUsers }) =>
          canOpenUsers
            ? "They do: the Noticeboard app is how Users receive Notices. Invite them on Users, and they join from there."
            : "The Noticeboard app is how parents receive Notices. An Admin invites them; you choose a Circle or Everyone when you send.",
      ],
      link: (_, { canOpenUsers }) => (canOpenUsers ? openUsers() : openNotices()),
    },
  }),
  // What can parents see?
  defineEntry({
    id: "concepts.whatParentsSee",
    sidebarItem: "notices",
    examples: [
      "What can parents see?",
      "What do parents see in the app?",
      "What does a parent see on Noticeboard?",
      "What can a User see?",
      "Can parents see other circles?",
      "What notices do parents see?",
      "Do parents see the dashboard?",
      "What can users see in the app?",
      "What's visible to parents?",
      "What do families see when they open the app?",
      "How do parents know they received one?",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () =>
          "Parents are Users: in the app they see the Notices sent to their own Circles or to Everyone, and Events on their calendar. They don't see your dashboard or other Circles.",
        () =>
          "A parent only sees, in the app, what's sent to Everyone or to a Circle they're in, plus Events on their calendar. The dashboard and other Circles stay out of view.",
        () =>
          "In the app, parents only see Notices sent to their Circles or to Everyone, and the Events that go with them. Nothing from the dashboard, and nothing from Circles they're not in.",
      ],
      link: openUsers,
    },
  }),
];

export const conceptsUsersEntries = [];

export const conceptsCirclesEntries = [];

export const conceptsDriveEntries = [
  // Drive vs Library
  defineEntry<{ canOpenLibrary: boolean }>({
    id: "concepts.driveVsLibrary",
    sidebarItem: "drive",
    examples: [
      "What is the difference between Drive and the Library?",
      "What's the difference between Drive and Library?",
      "What's the difference between the Library and Drive?",
      "Is Drive the same as the Library?",
      "What is Drive?",
      "What is the Library?",
      "Should I use Drive or the Library?",
      "Where do my own files go, Drive or Library?",
      "Where is the Library?",
      "drive vs library",
      "are worksheets in drive",
      "Where do I put a file?",
      "Are there other resources?",
      "We don't want the school worksheets",
      ...askedWhat("the difference between Drive and Library"),
    ],
    answer: {
      load: async (_, ctx) => ({ canOpenLibrary: canOpenSidebarItem(getSidebarItem("library"), ctx.user) }),
      templates: [
        ({ canOpenLibrary }) =>
          `Drive is your Organization's own files, and it's in the sidebar. The Library is ready-made worksheets and resources.${canOpenLibrary ? "" : " It isn't in your sidebar."}`,
        ({ canOpenLibrary }) =>
          `Drive holds files you've uploaded and shared. The Library is Noticeboard's ready-made worksheets and resources.${canOpenLibrary ? "" : " This Organization doesn't show Library."}`,
        ({ canOpenLibrary }) =>
          `Use Drive for your own files.${canOpenLibrary ? " The Library is separate, for ready-made worksheets and resources." : " Library isn't shown for this Organization."}`,
      ],
      link: openDrive,
    },
  }),
];

export const conceptsHelpCenterEntries = [
  // Is there a course to learn Noticeboard?
  defineEntry({
    id: "concepts.fundamentals",
    sidebarItem: "helpCenter",
    examples: [
      "Is there a course I can do to learn Noticeboard?",
      "Is there a course for beginners?",
      "Is there training for new teachers?",
      "Is there a course to learn Noticeboard?",
      "Where can I learn how to use Noticeboard?",
      "Is there training for Noticeboard?",
      "Can I take a course on Noticeboard?",
      "Is there a Noticeboard Fundamentals course?",
      "How do I learn Noticeboard properly?",
      "Is there a tutorial course for new staff?",
      "Where do I start learning Noticeboard?",
      "Can new staff do a Noticeboard course?",
      "is there training",
      "noticeboard fundamentals",
      "How many teachers completed the course?",
      "do you know everything thats in the fundamentals course?",
      "do you know everything that's in the fundamentals course?",
      ...askedWhat("the difference between you and the course"),
      "What's the difference between you and the course?",
      "the course is not this chat",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        (_, __, asked) =>
          withChatApart(
            "Yes — Noticeboard Fundamentals is a short course that walks through Create, Notices, Calendar and more, lesson by lesson.",
            asked
          ),
        (_, __, asked) =>
          withChatApart("There's Noticeboard Fundamentals, a self-paced course covering the main parts of Noticeboard.", asked),
        (_, __, asked) =>
          withChatApart(
            "Noticeboard Fundamentals is built for exactly that: a short, lesson-by-lesson course through the main features.",
            asked
          ),
      ],
      link: () => ({ label: "Take Noticeboard Fundamentals", href: COURSE_PATH }),
    },
  }),
];
